import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@eika/button';
import { InfoCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { useConfig } from '../../../defaultConfig';
import { paymentFromSelector, useAccounts } from '../../../fetchHooks/useAccount';
import { useCrossBorderStatus } from '../../../fetchHooks/useCrossBorderStatus';
import useTranslation from '../../../hooks/useTranslation';
import { useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { ErrorCardWrapper } from '../../shared/ErrorCardWrapper';
import AccountList from '../AccountList';
import AccountListCrossBorderPaymentSkeleton from './AccountListCrossBorderPaymentSkeleton';
import { CrossBorderPaymentActionType } from './form/actions';
import useFormContext from './form/useFormContext';

import './AccountListCrossBorderPayment.scss';

const AccountListCrossBorderPayment: React.FC = () => {
    const translated = useTranslation();
    const navigate = useNavigate();
    const { contactPageLink, createAccountsLink, priceListLink, featureToggles } = useConfig();

    const { data: accounts, ...accountState } = useAccounts({ select: paymentFromSelector });
    const { data: status, isLoading: loadingStatus, error: statusError, refetch } = useCrossBorderStatus();
    const { handleChange } = useFormContext();
    const { signingStatus, dispatch } = useSigningContext();

    useEffect(() => {
        if (signingStatus === 'COMPLETED') {
            dispatch({ type: Actions.RESET });
            handleChange({
                type: CrossBorderPaymentActionType.RESET,
            });
        }
    }, [dispatch, handleChange, signingStatus]);

    const retry = () => {
        if (statusError) {
            refetch();
        }
        if (accountState.error) {
            accountState.refetch();
        }
    };

    if (accountState.isLoading || loadingStatus) {
        return <AccountListCrossBorderPaymentSkeleton />;
    }

    const error = accountState.error || statusError;

    if (error) {
        return (
            <ErrorCardWrapper exception={error} isModal>
                <Button size="small" onClick={retry}>
                    {translated.tryAgain}
                </Button>
            </ErrorCardWrapper>
        );
    }

    if (status?.blocked || !status) {
        return (
            <InfoCard skipBackground>
                <RichIcon type={RichIconTypes.JORDKLODE} />
                <InfoCard.TitleH2>{translated.crossBorderPayment.statusTitle}</InfoCard.TitleH2>
                <p>{translated.crossBorderPayment.statusDescription}</p>
                {contactPageLink && (
                    <Link href={contactPageLink?.url} linkType={LinkType.BUTTON_SMALL_POSITIVE}>
                        {translated.crossBorderPayment.statusButton}
                    </Link>
                )}
            </InfoCard>
        );
    }

    if (!accounts) {
        return (
            <InfoCard skipBackground>
                <RichIcon type={RichIconTypes.BAAT_M_SOL} />
                <InfoCard.TitleH2>{translated.crossBorderPayment.accountList.noAccountsTitle}</InfoCard.TitleH2>
                <p>{translated.crossBorderPayment.accountList.noAccountsDescription}</p>
                {createAccountsLink && !featureToggles.disableAccountCreation && (
                    <Link
                        className="cross-border-payment__account-list-no-account-btn"
                        href={createAccountsLink?.url}
                        linkType={LinkType.BUTTON_SMALL_POSITIVE}
                    >
                        {translated.crossBorderPayment.accountList.createAccountLink}
                    </Link>
                )}
            </InfoCard>
        );
    }

    return (
        <div>
            <div className="cross-border-payment__account-list-info">
                <RichIcon type={RichIconTypes.JORDKLODE} className="cross-border-payment__account-list-info-img" />
                <div className="cross-border-payment__account-list-info-text">
                    <p>{translated.crossBorderPayment.accountList.description}</p>
                    {priceListLink && (
                        <Link href={priceListLink?.url} icon={IconTypes.EKSTERN_24} iconOnRightSide>
                            {translated.crossBorderPayment.accountList.priceListLink}
                        </Link>
                    )}
                </div>
            </div>
            <h2 className="cross-border-payment__account-list-title">
                {translated.crossBorderPayment.accountList.payFrom}
            </h2>
            <p className="cross-border-payment__account-list-label">
                {translated.crossBorderPayment.accountList.label}
            </p>
            <div className="cross-border-payment__account-list">
                <AccountList
                    error={accountState.error ?? undefined}
                    retry={accountState.refetch}
                    accounts={accounts}
                    onClick={(accountNumber) => {
                        handleChange({
                            type: CrossBorderPaymentActionType.FROM_ACCOUNT_SELECTED,
                            payload: accountNumber,
                        });
                        navigate('mottaker-liste');
                    }}
                />
            </div>
        </div>
    );
};

export default AccountListCrossBorderPayment;
